<template>
  <article class="product-card">
    <div
      class="image-wrap"
      :role="hasGallery ? 'group' : null"
      :aria-roledescription="hasGallery ? 'carrusel' : null"
      :aria-label="hasGallery ? `Imágenes de ${product.name}` : null"
    >
      <!-- Skeleton mientras carga la foto: evita el salto de layout y el
           hueco en blanco del catálogo (plan 22_08_2026, R9). -->
      <div v-if="images.length && !imageLoaded[activeImage]" class="skeleton" aria-hidden="true"></div>
      <!-- Las fotos se montan todas y se ocultan con opacidad, NUNCA con
           display:none: una imagen sin caja de layout no intersecta el
           viewport, así que `loading="lazy"` nunca dispara la descarga y la
           ficha se queda en blanco para siempre (ese era el motivo de que el
           catálogo no mostrara la foto y el modal sí). Con opacidad la carga
           diferida vuelve a funcionar: las fichas fuera de pantalla no
           descargan nada hasta que el carrusel las trae al viewport. -->
      <img
        v-for="(url, index) in images"
        :key="url"
        class="slide"
        :class="{ 'is-active': index === activeImage }"
        :src="url"
        :alt="imageAlt(index)"
        loading="lazy"
        decoding="async"
        :aria-hidden="index === activeImage ? null : 'true'"
        @load="markLoaded(index)"
        @error="markLoaded(index)"
      />

      <template v-if="hasGallery">
        <button
          type="button"
          class="img-nav img-prev"
          aria-label="Imagen anterior"
          @click.stop="step(-1)"
        >
          ‹
        </button>
        <button
          type="button"
          class="img-nav img-next"
          aria-label="Imagen siguiente"
          @click.stop="step(1)"
        >
          ›
        </button>
        <ol class="img-dots">
          <li v-for="(url, index) in images" :key="url">
            <button
              type="button"
              class="img-dot"
              :class="{ 'is-active': index === activeImage }"
              :aria-label="`Ver imagen ${index + 1} de ${images.length}`"
              :aria-current="index === activeImage ? 'true' : 'false'"
              @click.stop="activeImage = index"
            ></button>
          </li>
        </ol>
      </template>
    </div>
    <div class="body">
      <h3 class="name">{{ product.name }}</h3>
      <p class="description">{{ product.description }}</p>
      <ul class="meta">
        <li>
          <span class="meta-label">Material:</span>
          <span class="meta-value">{{ materialName }}</span>
        </li>
        <li>
          <span class="meta-label">Tamaño:</span>
          <span class="meta-value">{{ sizeLabel }}</span>
        </li>
      </ul>
      <p class="price">
        Desde <strong>{{ formattedPrice }}</strong>
      </p>

      <div class="actions">
        <button type="button" class="order-btn" @click="$emit('order-clicked', product)">
          Pedir ahora
        </button>
        <WhatsAppButton
          variant="inline"
          label="Pedir por WhatsApp"
          :message="whatsappMessage"
          class="wa-card"
        />
      </div>
    </div>
  </article>
</template>

<script>
import { formatCurrencyRAE } from '@/utils/numberFormat';
import WhatsAppButton from '../WhatsAppButton.vue';

export default {
  name: 'ProductCard',
  components: { WhatsAppButton },
  props: {
    product: { type: Object, required: true },
    materialName: { type: String, default: 'Por definir' },
  },
  emits: ['order-clicked'],
  data() {
    return {
      activeImage: 0,
      // Índices ya descargados: el skeleton sólo cubre la foto visible.
      imageLoaded: {},
    };
  },
  computed: {
    images() {
      const list = Array.isArray(this.product.images) ? this.product.images : [];
      // Compatibilidad con el shape previo a la galería (una sola imageUrl).
      if (list.length) return list.filter(Boolean);
      return this.product.imageUrl ? [this.product.imageUrl] : [];
    },
    hasGallery() {
      return this.images.length > 1;
    },
    sizeLabel() {
      const { x, y, z } = this.product.sizeMm || {};
      return `${x} × ${y} × ${z} mm`;
    },
    formattedPrice() {
      return formatCurrencyRAE(this.product.priceFrom || 0, this.product.currency || 'COP');
    },
    whatsappMessage() {
      return `Hola, me interesa el producto "${this.product.name}" del catálogo 3D.`;
    },
  },
  methods: {
    markLoaded(index) {
      this.imageLoaded[index] = true;
    },
    imageAlt(index) {
      if (!this.hasGallery) return this.product.name;
      return `${this.product.name} — imagen ${index + 1} de ${this.images.length}`;
    },
    // Avance circular: desde la última foto se vuelve a la primera.
    step(direction) {
      const total = this.images.length;
      if (total < 2) return;
      this.activeImage = (this.activeImage + direction + total) % total;
    },
  },
};
</script>

<style scoped>
.product-card {
  display: flex;
  flex-direction: column;
  /* Ancho fluido: en móvil se adapta al viewport para que el texto no se
     recorte, sin pasar de 300px en pantallas grandes (R7). */
  width: min(80vw, 300px);
  background: white;
  border: 1px solid #9DB2BF;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(11, 19, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.product-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px rgba(11, 19, 42, 0.12);
}

/* Proporción uniforme para todas las fichas. El marco es vertical 4/5 porque
   ésa es la proporción de los renders del catálogo (1080×1350): con el 4/3
   apaisado anterior la foto se recortaba por arriba y por abajo. Así la pieza
   se ve entera y sin bandas vacías. */
.image-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  background: #F8FAFC;
  overflow: hidden;
}

/* Las fotos se apilan en el mismo marco y sólo cambia la opacidad: así todas
   conservan caja de layout y `loading="lazy"` puede resolverlas. */
.image-wrap .slide {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* `contain` y no `cover`: la foto del producto no se recorta nunca, aunque
     su proporción no coincida con la del marco. */
  object-fit: contain;
  display: block;
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
}

.image-wrap .slide.is-active {
  opacity: 1;
}

.img-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid rgba(157, 178, 191, 0.8);
  background: rgba(255, 255, 255, 0.85);
  color: #526D82;
  font-size: 1.2rem;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s;
}

.img-prev { left: 6px; }
.img-next { right: 6px; }

/* En escritorio las flechas aparecen al pasar el cursor; en táctil siempre
   están visibles porque no hay hover que las revele. */
.product-card:hover .img-nav,
.img-nav:focus-visible {
  opacity: 1;
}

.img-nav:hover {
  background: white;
}

.img-nav:focus-visible {
  outline: 2px solid #3D566B;
  outline-offset: 2px;
}

.img-dots {
  position: absolute;
  bottom: 6px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 5px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.img-dot {
  width: 7px;
  height: 7px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(82, 109, 130, 0.7);
  background: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}

.img-dot.is-active {
  background: #2E8A99;
  border-color: #2E8A99;
  transform: scale(1.25);
}

.img-dot:focus-visible {
  outline: 2px solid #3D566B;
  outline-offset: 2px;
}

@media (hover: none) {
  .img-nav { opacity: 1; }
}

.skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #EEF2F6 25%, #E2E8F0 37%, #EEF2F6 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton { animation: none; }
}

.body {
  padding: 1rem 1.2rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
}

.name {
  font-family: "Inter", sans-serif;
  font-size: 1.15rem;
  color: #111827;
  margin: 0;
}

.description {
  font-family: "Inter", sans-serif;
  color: #4F5665;
  font-size: 0.95rem;
  line-height: 1.4;
  margin: 0;
  flex: 1;
  /* Justificado como el resto del cuerpo de texto del sitio (.text en
     global.css). La partición de palabras es necesaria aquí y no allá: en una
     columna de ~250px, el español sin guiones abre ríos de espacio. */
  text-align: justify;
  -webkit-hyphens: auto;
  hyphens: auto;
}

.meta {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.9rem;
  color: #4F5665;
}

.meta li {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  /* Con nombres largos de material, la fila envuelve en vez de recortar. */
  flex-wrap: wrap;
  padding: 2px 0;
}

.meta-label {
  color: #4F5665;
}

.meta-value {
  color: #2C3E50;
  font-weight: 600;
}

.price {
  margin: 0;
  color: #2C3E50;
  font-size: 0.95rem;
}

.price strong {
  color: #526D82;
  font-size: 1.15rem;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.4rem;
}

.order-btn {
  width: 100%;
  padding: 0.5rem 0.8rem;
  background: #526D82;
  color: white;
  border: 1px solid #526D82;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.95rem;
  transition: background 0.2s, color 0.2s;
}

.order-btn:hover {
  background: #3D566B;
  border-color: #3D566B;
}

.order-btn:focus-visible {
  outline: 2px solid #3D566B;
  outline-offset: 2px;
}

/* El CTA de WhatsApp compartido se ajusta al tamaño de la ficha. */
.actions :deep(.whatsappInline) {
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.8rem;
  font-size: 0.95rem;
  border-radius: 8px;
  box-shadow: none;
}

.actions :deep(.whatsappInline):hover {
  transform: none;
  box-shadow: 0 4px 12px -4px rgba(37, 211, 102, 0.6);
}

.actions :deep(.whatsappInline) img {
  width: 18px;
  height: 18px;
}

@media (max-width: 800px) {
  /* Tarjeta más compacta en móvil: menos relleno y tipografías algo menores,
     de modo que el contenido quepa entero en 360px (R7). */
  .body {
    padding: 0.8rem 0.9rem 1rem;
    gap: 0.5rem;
  }

  .name {
    font-size: 1.05rem;
  }

  .description,
  .meta,
  .price {
    font-size: 0.9rem;
  }

  .order-btn,
  .actions :deep(.whatsappInline) {
    font-size: 0.9rem;
  }
}
</style>
